import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { CANDIDATE_STATUS, EVALUATION_ROUNDS } from '@/types/enums';
import { formatCandidateStatus, formatEvaluationRound } from '@/utils/formatters';
import { getCandidateStatusColor } from '@/utils/statusColors';

interface AssignedCandidateRow {
  id: string;
  full_name: string;
  email: string;
  status: string;
  current_round: string | null;
  created_at: string;
  updated_at: string;
  colleges?: { name: string } | null;
  job_positions?: { role_name: string } | null;
}

interface EvaluationHistoryRow {
  id: string;
  round_name: string;
  status: string | null;
  score: number | null;
  evaluated_at: string;
  candidates?: {
    id: string;
    full_name: string;
    created_at: string;
    colleges?: { name: string } | null;
    job_positions?: { role_name: string } | null;
  } | null;
}

const COMPLETED_PANELIST_STATUSES = new Set<string>([
  CANDIDATE_STATUS.PASSED,
  CANDIDATE_STATUS.SELECTED,
  CANDIDATE_STATUS.OFFERED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
]);

const AUTO_ASSIGNED_PANELIST_STATUSES = [
  CANDIDATE_STATUS.PASSED,
  CANDIDATE_STATUS.PENDING,
  CANDIDATE_STATUS.HOLD,
  CANDIDATE_STATUS.SELECTED,
  CANDIDATE_STATUS.OFFERED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
] as const;

export default function PanelistDashboard() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();
  const currentYearStr = currentYear.toString();
  const yearFilter = searchParams.get('year') ?? currentYearStr;

  const setYearFilter = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      if (!value || value === currentYearStr) {
        next.delete('year');
      } else {
        next.set('year', value);
      }

      return next;
    });
  };

  const yearOptions = useMemo(
    () => Array.from({ length: 5 }, (_, index) => (currentYear - index).toString()),
    [currentYear]
  );

  const yearBounds = useMemo(() => {
    if (yearFilter === 'all') return null;

    const year = Number.parseInt(yearFilter, 10);
    if (Number.isNaN(year)) return null;

    return {
      start: new Date(Date.UTC(year, 0, 1)).toISOString(),
      end: new Date(Date.UTC(year + 1, 0, 1)).toISOString(),
    };
  }, [yearFilter]);

  const isWithinYear = useCallback((date: string | null | undefined) => {
    if (!yearBounds) return true;
    if (!date) return false;
    return date >= yearBounds.start && date < yearBounds.end;
  }, [yearBounds]);

  const candidateListHref = useMemo(() => {
    const params = new URLSearchParams();

    if (yearFilter === 'all') {
      params.set('year', 'all');
    } else if (yearFilter !== currentYearStr) {
      params.set('year', yearFilter);
    }

    const query = params.toString();
    return query ? `/admin/candidates?${query}` : '/admin/candidates';
  }, [currentYearStr, yearFilter]);

  const { data: assignedJobPositionIds = [] } = useQuery({
    queryKey: ['panelist-job-positions', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [] as string[];

      const { data, error } = await supabase
        .from('job_positions')
        .select('id')
        .contains('interviewer', [profile.id])
        .eq('is_active', true);

      if (error) throw error;
      return (data ?? []).map((position) => position.id);
    },
    enabled: !!profile?.id,
  });

  const { data: assignedCandidates = [], isLoading: assignedLoading } = useQuery({
    queryKey: ['panelist-candidates', profile?.id, assignedJobPositionIds],
    queryFn: async () => {
      if (!profile?.id) return [] as AssignedCandidateRow[];

      const { data: directCandidates, error: directCandidatesError } = await supabase
        .from('candidates')
        .select(`
          id,
          full_name,
          email,
          status,
          current_round,
          created_at,
          updated_at,
          colleges:college_id!inner(name),
          job_positions:job_position_id(role_name)
        `)
        .eq('colleges.is_enabled', true)
        .eq('colleges.is_deleted', false)
        .eq('current_round', EVALUATION_ROUNDS.OFFICE_TECHNICAL)
        .contains('interviewer', [profile.id])
        .order('updated_at', { ascending: false });

      if (directCandidatesError) throw directCandidatesError;

      if (assignedJobPositionIds.length === 0) {
        return (directCandidates ?? []) as AssignedCandidateRow[];
      }

      const { data: roleCandidates, error: roleCandidatesError } = await supabase
        .from('candidates')
        .select(`
          id,
          full_name,
          email,
          status,
          current_round,
          created_at,
          updated_at,
          colleges:college_id!inner(name),
          job_positions:job_position_id(role_name)
        `)
        .eq('colleges.is_enabled', true)
        .eq('colleges.is_deleted', false)
        .eq('current_round', EVALUATION_ROUNDS.OFFICE_TECHNICAL)
        .in('job_position_id', assignedJobPositionIds)
        .in('status', [...AUTO_ASSIGNED_PANELIST_STATUSES])
        .order('updated_at', { ascending: false });

      if (roleCandidatesError) throw roleCandidatesError;

      const merged = new Map<string, AssignedCandidateRow>();
      ((directCandidates ?? []) as AssignedCandidateRow[]).forEach((candidate) => {
        merged.set(candidate.id, candidate);
      });
      ((roleCandidates ?? []) as AssignedCandidateRow[]).forEach((candidate) => {
        if (!merged.has(candidate.id)) {
          merged.set(candidate.id, candidate);
        }
      });

      return Array.from(merged.values()).sort((a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
    },
    enabled: !!profile?.id,
  });

  const { data: recentEvaluations = [], isLoading: evaluationsLoading } = useQuery({
    queryKey: ['panelist-evaluations', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [] as EvaluationHistoryRow[];

      const { data, error } = await supabase
        .from('evaluation_rounds')
        .select(`
          id,
          round_name,
          status,
          score,
          evaluated_at,
          candidates:candidate_id(
            id,
            full_name,
            created_at,
            colleges:college_id(name),
            job_positions:job_position_id(role_name)
          )
        `)
        .eq('evaluator_id', profile.id)
        .order('evaluated_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data ?? []) as EvaluationHistoryRow[];
    },
    enabled: !!profile?.id,
  });

  const filteredAssignedCandidates = useMemo(
    () => assignedCandidates.filter((candidate) => isWithinYear(candidate.created_at)),
    [assignedCandidates, isWithinYear]
  );

  const filteredRecentEvaluations = useMemo(
    () => recentEvaluations.filter((evaluation) => isWithinYear(evaluation.candidates?.created_at)),
    [isWithinYear, recentEvaluations]
  );

  const stats = useMemo(() => {
    const pendingTechnical = filteredAssignedCandidates.filter((candidate) => (
      candidate.current_round === EVALUATION_ROUNDS.OFFICE_TECHNICAL
      && !COMPLETED_PANELIST_STATUSES.has(candidate.status)
    )).length;

    const movedForward = filteredAssignedCandidates.filter((candidate) => (
      candidate.current_round === EVALUATION_ROUNDS.HR_FOUNDER
      || COMPLETED_PANELIST_STATUSES.has(candidate.status)
    )).length;

    const holds = filteredAssignedCandidates.filter((candidate) => candidate.status === CANDIDATE_STATUS.HOLD).length;

    return [
      {
        label: 'Assigned Candidates',
        value: filteredAssignedCandidates.length,
        description: 'Candidates assigned to you for interview or follow-up.',
      },
      {
        label: 'Pending Interviews',
        value: pendingTechnical,
        description: 'Assigned candidates waiting for your evaluation.',
      },
      {
        label: 'Advanced Candidates',
        value: movedForward,
        description: 'Candidates who cleared your round or moved to later stages.',
      },
      {
        label: 'Recent Evaluations',
        value: filteredRecentEvaluations.length,
        description: holds > 0
          ? `${holds} assigned candidate${holds === 1 ? '' : 's'} ${holds === 1 ? 'is' : 'are'} currently on hold.`
          : 'Your latest completed evaluations.',
      },
    ];
  }, [filteredAssignedCandidates, filteredRecentEvaluations.length]);

  if (assignedLoading || evaluationsLoading) {
    return <LoadingSpinner size="lg" text="Loading dashboard..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Panelist Dashboard</h1>
          <p className="text-muted-foreground">
            Review assigned candidates, pending interviews, and recent evaluation activity.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm sm:w-auto"
            value={yearFilter}
            onChange={(event) => setYearFilter(event.target.value)}
          >
            <option value="all">All Years</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <Button asChild variant="outline" className="w-full sm:w-auto">
            <Link to={candidateListHref}>View Candidate List</Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{stat.label}</CardTitle>
              <CardDescription>{stat.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Assigned Candidates</CardTitle>
            <CardDescription>
              Candidates currently assigned to you for evaluation or follow-up.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 md:hidden">
              {filteredAssignedCandidates.length === 0 ? (
                <div className="rounded-lg border px-4 py-6 text-center text-sm text-muted-foreground">
                  No assigned candidates were found for the selected year.
                </div>
              ) : (
                filteredAssignedCandidates.slice(0, 8).map((candidate) => (
                  <Link
                    key={candidate.id}
                    to={`/admin/candidates/${candidate.id}/evaluate`}
                    className="block rounded-lg border p-4 space-y-3 hover:bg-muted/50 transition-colors"
                  >
                    <div>
                      <div className="font-medium">{candidate.full_name}</div>
                      <div className="text-xs text-muted-foreground break-all">{candidate.email}</div>
                    </div>

                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <div className="text-xs text-muted-foreground">College</div>
                        <div>{candidate.colleges?.name || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Role</div>
                        <div className="break-words">{candidate.job_positions?.role_name || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Status</div>
                        <Badge className={getCandidateStatusColor(candidate.status)}>
                          {formatCandidateStatus(candidate.status)}
                        </Badge>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Round</div>
                        <div>{formatEvaluationRound(candidate.current_round)}</div>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>College</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Round</TableHead>
                </TableRow>
              </TableHeader>
                <TableBody>
                  {filteredAssignedCandidates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        No assigned candidates were found for the selected year.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAssignedCandidates.slice(0, 8).map((candidate) => (
                      <TableRow
                        key={candidate.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/admin/candidates/${candidate.id}/evaluate`)}
                      >
                        <TableCell>
                          <div className="font-medium">{candidate.full_name}</div>
                          <div className="text-xs text-muted-foreground break-all">{candidate.email}</div>
                        </TableCell>
                        <TableCell>{candidate.colleges?.name || 'N/A'}</TableCell>
                        <TableCell className="max-w-xs break-words">{candidate.job_positions?.role_name || 'N/A'}</TableCell>
                        <TableCell>
                          <Badge className={getCandidateStatusColor(candidate.status)}>
                            {formatCandidateStatus(candidate.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatEvaluationRound(candidate.current_round)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Evaluations</CardTitle>
            <CardDescription>
              Your most recent completed evaluations.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 md:hidden">
              {filteredRecentEvaluations.length === 0 ? (
                <div className="rounded-lg border px-4 py-6 text-center text-sm text-muted-foreground">
                  No evaluation history was found for the selected year.
                </div>
              ) : (
                filteredRecentEvaluations.slice(0, 8).map((evaluation) => (
                  <div key={evaluation.id} className="rounded-lg border p-4 space-y-3">
                    <div>
                      <div className="font-medium">{evaluation.candidates?.full_name || 'Unknown'}</div>
                      <div className="text-xs text-muted-foreground break-words">
                        {evaluation.candidates?.job_positions?.role_name || 'N/A'}
                        {' · '}
                        {evaluation.candidates?.colleges?.name || 'N/A'}
                      </div>
                    </div>

                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <div className="text-xs text-muted-foreground">Round</div>
                        <div>{formatEvaluationRound(evaluation.round_name)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Status</div>
                        <Badge className={getCandidateStatusColor(evaluation.status || '')}>
                          {formatCandidateStatus(evaluation.status)}
                        </Badge>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Score</div>
                        <div>{evaluation.score ?? '-'}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Round</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                </TableRow>
              </TableHeader>
                <TableBody>
                  {filteredRecentEvaluations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        No evaluation history was found for the selected year.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRecentEvaluations.slice(0, 8).map((evaluation) => (
                      <TableRow key={evaluation.id}>
                        <TableCell>
                          <div className="font-medium">{evaluation.candidates?.full_name || 'Unknown'}</div>
                          <div className="text-xs text-muted-foreground break-words">
                            {evaluation.candidates?.job_positions?.role_name || 'N/A'}
                            {' · '}
                            {evaluation.candidates?.colleges?.name || 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell>{formatEvaluationRound(evaluation.round_name)}</TableCell>
                        <TableCell>
                          <Badge className={getCandidateStatusColor(evaluation.status || '')}>
                            {formatCandidateStatus(evaluation.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {evaluation.score ?? '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
